import React, { useState, useEffect, useContext } from "react";
import { Navigate } from "react-router-dom";
import { API_BASE_URL } from "../config";
import { AuthContext } from "../contexts/AuthContext";
import dayjs from "dayjs";


export default function AdminDashboard() {
    const { fetchWithAuth } = useContext(AuthContext);

    const { isLoggedIn, userName, userRole, login, logout } = useContext(AuthContext);

    const [redirect, setRedirect] = useState(false);
    const [formData, setFormData] = useState({
        title: "",
        content: "",
        targetRoles: [],
        actionDate: "",
    });
    const [showPreview, setShowPreview] = useState(false);
    const [messages, setMessages] = useState([]);
    const [editingId, setEditingId] = useState(null);
    const [fetchError, setFetchError] = useState(null);
    const [showLogsModal, setShowLogsModal] = useState(false);
    const [messageLogs, setMessageLogs] = useState([]);
    const [selectedMessageTitle, setSelectedMessageTitle] = useState("");
    const [pagination, setPagination] = useState({
        current_page: 1,
        last_page: 1,
    });

    // ---- Helpers to parse various response shapes ----
    // fetchWithAuth may return a Response or parsed JSON. Normalize to JSON.
    async function normalizeFetchResult(result) {
        // If it's a Response-like object with .json()
        if (result && typeof result.json === "function") {
            // If Response, check ok and parse
            if (typeof result.ok === "boolean" && !result.ok) {
                // try to extract message
                let text;
                try {
                    text = await result.text();
                } catch {
                    text = `HTTP ${result.status}`;
                }
                throw new Error(text || `Request failed with status ${result.status}`);
            }
            return await result.json();
        }

        // Otherwise assume already-parsed JSON
        return result ?? {};
    }

    // Safely extract an array of messages from many possible API shapes
    function extractMessages(payload) {
        if (!payload) return [];
        if (Array.isArray(payload)) return payload;
        if (Array.isArray(payload.data)) return payload.data;
        if (Array.isArray(payload.results)) return payload.results;
        // Some APIs nest under payload.payload.data etc.
        if (payload.payload && Array.isArray(payload.payload.data)) return payload.payload.data;
        return [];
    }

    // Safely extract pagination info with fallbacks
    function extractPagination(payload) {
        const page = Number(payload?.current_page ?? payload?.page ?? payload?.meta?.current_page ?? 1) || 1;
        const last = Number(payload?.last_page ?? payload?.lastPage ?? payload?.meta?.last_page) || null;

        // If last missing but total/per_page available, compute it
        if (!last && payload?.total && payload?.per_page) {
            const computed = Math.max(1, Math.ceil(Number(payload.total) / Number(payload.per_page)));
            return { current_page: page, last_page: computed };
        }

        return {
            current_page: page,
            last_page: last || 1,
        };
    }

    // ---- Fetch messages ----
    const fetchMessages = async (page = 1) => {
        try {
            setFetchError(null);

            const resOrJson = await fetchWithAuth(`/api/messages?page=${page}&per_page=5`);
            const payload = await normalizeFetchResult(resOrJson);

            const msgs = extractMessages(payload);
            const pag = extractPagination(payload);

            setMessages(msgs);
            setPagination(pag);
        } catch (err) {
            console.error("fetchMessages error:", err);
            setFetchError(err?.message || "Failed to load messages");
            // safe fallbacks
            setMessages([]);
            setPagination({ current_page: 1, last_page: 1 });
        }
    };

    useEffect(() => {
        fetchMessages();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // ---- Edit / form handlers ----

    const handleEdit = (msg) => {
        // Convert action_date to YYYY-MM-DD safely using dayjs
        const formattedDate = msg.action_date
            ? dayjs(msg.action_date).format("YYYY-MM-DD")
            : "";

        setEditingId(msg.id);
        setFormData({
            title: msg.title ?? "",
            content: msg.content ?? "",
            targetRoles: Array.isArray(msg.target_roles) ? msg.target_roles : [],
            actionDate: formattedDate,  // <-- updated
        });
        window.scrollTo({ top: 0, behavior: "smooth" });
    };


    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const handleRoleChange = (e) => {
        const { value, checked } = e.target;
        setFormData((prev) => ({
            ...prev,
            targetRoles: checked ? [...prev.targetRoles, value] : prev.targetRoles.filter((r) => r !== value),
        }));
    };

    const handlePreview = () => setShowPreview(true);

    const handleSave = async () => {
        try {
            const url = editingId ? `/api/messages/${editingId}` : `/api/messages`;
            const method = editingId ? "PUT" : "POST";

            const resOrJson = await fetchWithAuth(url, {
                method,
                body: JSON.stringify({
                    title: formData.title,
                    content: formData.content,
                    target_roles: formData.targetRoles,
                    action_date: formData.actionDate,
                }),
                headers: {
                    "Content-Type": "application/json",
                },
            });

            // Normalize to detect errors if fetchWithAuth returns Response
            await normalizeFetchResult(resOrJson);

            alert(editingId ? "Message updated successfully!" : "Message created successfully!");
            setShowPreview(false);
            setFormData({ title: "", content: "", targetRoles: [], actionDate: "" });
            setEditingId(null);
            fetchMessages();
        } catch (error) {
            console.error("handleSave error:", error);
            alert(error?.message || "Failed to save message");
        }
    };

    // ---- Logs ----
    const handleViewLogs = async (messageId, title) => {
        try {
            const resOrJson = await fetchWithAuth(
                `/api/messages/${messageId}/logs`
            );
            const payload = await normalizeFetchResult(resOrJson);

            const logs = (payload.acknowledgements || []).map(log => ({
                username: log.username || "N/A",
                role: log.role || "N/A",
                ipAddress: log.ip_address || "N/A",
                acknowledgedAt: dayjs(log.acknowledged_at)
            }));

            setMessageLogs(logs);
            setSelectedMessageTitle(title);
            setShowLogsModal(true);
        } catch (error) {
            console.error("handleViewLogs error:", error);
            alert(error?.message || "Failed to load logs");
        }
    };

    const downloadLogsCSV = () => {
        if (!messageLogs.length) return;

        const headers = ["Username", "Role", "IP Address", "Acknowledged At"];
        const rows = messageLogs.map(log => [
            log.username,
            log.role,
            log.ipAddress,
            log.acknowledgedAt.format("YYYY-MM-DD HH:mm:ss")
        ]);

        const csvContent =
            [headers, ...rows]
                .map(row => row.map(val => `"${val}"`).join(","))
                .join("\n");

        const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
        const url = URL.createObjectURL(blob);

        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", `${selectedMessageTitle.replace(/\s+/g, "_")}_logs.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    if (redirect) return <Navigate to="/" replace />;

    // ---- Render ----
    return (
        <div className="max-w-3xl mx-auto px-4 py-10 text-[#144E73]">
            <h1 className="text-3xl font-bold mb-8 text-center">Create New Message</h1>

            {fetchError && (
                <div className="mb-6 p-4 bg-red-100 border border-red-400 text-red-700 rounded">
                    <strong>Error:</strong> {fetchError}
                </div>
            )}

            {/* Form */}
            <form className="space-y-6">
                <div>
                    <label className="block mb-1 font-semibold">Title</label>
                    <input
                        type="text"
                        name="title"
                        value={formData.title}
                        onChange={handleChange}
                        className="w-full px-4 py-2 border rounded focus:ring-[#F29E38] focus:outline-none"
                    />
                </div>

                <div>
                    <label className="block mb-1 font-semibold">Content</label>
                    <textarea
                        name="content"
                        rows="5"
                        value={formData.content}
                        onChange={handleChange}
                        className="w-full px-4 py-2 border rounded focus:ring-[#F29E38] focus:outline-none"
                    />
                </div>

                <div>
                    <label className="block mb-2 font-semibold">Target Roles</label>
                    <div className="flex gap-6">
                        {["Admin", "Head Office", "Supplier"].map((role) => (
                            <label key={role} className="flex items-center gap-2 capitalize">
                                <input type="checkbox" value={role} checked={formData.targetRoles.includes(role)} onChange={handleRoleChange} />
                                {role.replace("_", " ")}
                            </label>
                        ))}
                    </div>
                </div>

                <div>
                    <label className="block mb-1 font-semibold">Action Date</label>
                    <input
                        type="date"
                        name="actionDate"
                        value={formData.actionDate}
                        onChange={handleChange}
                        className="px-4 py-2 border rounded focus:ring-[#F29E38] focus:outline-none"
                    />
                </div>

                <div className="flex gap-4 mt-6">
                    <button type="button" onClick={handlePreview} className="bg-[#144E73] text-white px-6 py-2 rounded-full font-semibold hover:bg-[#0f3a5a] transition">
                        Preview
                    </button>
                    <button type="button" onClick={handleSave} className="bg-[#F29E38] text-[#023059] px-6 py-2 rounded-full font-semibold hover:bg-[#e58e22] transition">
                        Save
                    </button>
                </div>
            </form>

            {/* Preview */}
            {showPreview && (
                <div className="mt-10 p-6 border border-[#144E73] rounded-lg bg-[#F2F1F0] shadow-lg">
                    <h2 className="text-2xl font-bold text-[#144E73] mb-4">{formData.title}</h2>
                    <p className="text-[#023059] mb-4 whitespace-pre-wrap">{formData.content}</p>
                    <p className="text-sm text-gray-600 italic">
                        For roles: {formData.targetRoles.join(", ") || "None selected"} <br />
                        Action date: {formData.actionDate ? new Date(formData.actionDate).toLocaleDateString(undefined, {
                        year: "numeric",
                        month: "short",
                        day: "numeric"
                    }) : "Not set"}
                    </p>
                </div>
            )}

            {/* Existing Messages */}
            <div className="mt-12">
                <h2 className="text-2xl font-bold mb-4 text-[#144E73]">Existing Messages</h2>

                {(messages?.length ?? 0) === 0 ? (
                    <p className="text-gray-600">No messages found.</p>
                ) : (
                    <ul className="space-y-4">
                        {messages.map((msg) => {
                            const isEditable = msg?.action_date ? dayjs(msg.action_date.replace(" ", "T")).isAfter(dayjs().subtract(1, "day")) : false;
                            return (
                                <li key={msg.id} className="p-4 bg-white rounded-lg shadow border border-gray-200">
                                    <div className="flex justify-between items-center">
                                        <h3 className="text-lg font-semibold text-[#023059]">{msg.title ?? "Untitled"}</h3>
                                        <span className={`px-3 py-1 text-sm rounded-full ${msg.status === "Acknowledged" ? "bg-green-100 text-green-700" : "bg-yellow-100 text-yellow-700"}`}>
                      {msg.status ?? "Unknown"}
                    </span>
                                    </div>

                                    <p className="mb-2 text-gray-800 whitespace-pre-wrap">{msg.content ?? ""}</p>
                                    <p className="text-sm text-gray-600 italic">
                                        Roles: {Array.isArray(msg.target_roles) ? msg.target_roles.join(", ") : "None"}
                                        <br />
                                        <p>
                                            Action Date: {dayjs(msg.action_date).format("MMMM D, YYYY")}
                                        </p>

                                    </p>

                                    <div className="flex gap-2 mt-2">
                                        {isEditable && (
                                            <button onClick={() => handleEdit(msg)} className="bg-blue-600 text-white px-4 py-1 rounded hover:bg-blue-700">
                                                Edit
                                            </button>
                                        )}

                                        <button onClick={() => handleViewLogs(msg.id, msg.title ?? "Untitled")} className="bg-gray-600 text-white px-4 py-1 rounded hover:bg-gray-700">
                                            View Logs
                                        </button>
                                    </div>
                                </li>
                            );
                        })}
                    </ul>
                )}
            </div>

            {/* Pagination Controls */}
            {Number(pagination.last_page) > 1 && (
                <div className="flex justify-center mt-6 gap-2">
                    <button disabled={Number(pagination.current_page) === 1} onClick={() => fetchMessages(Number(pagination.current_page) - 1)} className="px-4 py-2 bg-gray-200 rounded disabled:opacity-50">
                        Previous
                    </button>

                    <span className="px-4 py-2">
            Page {pagination.current_page} of {pagination.last_page}
          </span>

                    <button disabled={Number(pagination.current_page) === Number(pagination.last_page)} onClick={() => fetchMessages(Number(pagination.current_page) + 1)} className="px-4 py-2 bg-gray-200 rounded disabled:opacity-50">
                        Next
                    </button>
                </div>
            )}

            {/* Logs Modal */}
            {showLogsModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg p-6 shadow-lg max-w-2xl w-full">
                        <div className="flex justify-between items-center mb-4">
                            <h2 className="text-lg font-bold">
                                Logs for: {selectedMessageTitle}
                            </h2>
                            {messageLogs.length > 0 && (
                                <button
                                    onClick={downloadLogsCSV}
                                    className="px-3 py-1 bg-green-600 text-white rounded hover:bg-green-700"
                                >
                                    Download CSV
                                </button>
                            )}
                        </div>

                        {messageLogs.length > 0 ? (
                            <table className="w-full border-collapse border border-gray-300">
                                <thead>
                                <tr className="bg-gray-100">
                                    <th className="border border-gray-300 px-4 py-2">Username</th>
                                    <th className="border border-gray-300 px-4 py-2">Role</th>
                                    <th className="border border-gray-300 px-4 py-2">IP Address</th>
                                    <th className="border border-gray-300 px-4 py-2">Acknowledged At</th>
                                </tr>
                                </thead>
                                <tbody>
                                {messageLogs.map((log, index) => (
                                    <tr key={index}>
                                        <td className="border border-gray-300 px-4 py-2">{log.username}</td>
                                        <td className="border border-gray-300 px-4 py-2">{log.role}</td>
                                        <td className="border border-gray-300 px-4 py-2">{log.ipAddress}</td>
                                        <td className="border border-gray-300 px-4 py-2">
                                            {log.acknowledgedAt.format("YYYY-MM-DD HH:mm:ss")}
                                        </td>
                                    </tr>
                                ))}
                                </tbody>
                            </table>
                        ) : (
                            <p className="text-gray-600">No logs found.</p>
                        )}

                        <div className="flex justify-end mt-4">
                            <button
                                onClick={() => setShowLogsModal(false)}
                                className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}

        </div>
    );
}
